import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandLess,
  ExpandMore,
  GetApp,
} from "@material-ui/icons";
import Autocomplete from "@material-ui/lab/Autocomplete";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import DialogAppBar from "../../../Components/DialogAppBar";
import StockItemSearch from "../../../Components/StockItems/StockItemSearch";
import Title from "../../../Components/Title";
import {
  formatGoogleDriveMediaLink,
  getToken,
  requestErrorHandler,
} from "../../../Utils/Common";
import EnvSettings from "../../../Utils/EnvSettings";
import { showPopupSnackbar } from "../../../redux/actions/snackbarActions";

const server = EnvSettings.server;

const formatPrice = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return numeric.toFixed(2);
  }
  return value;
};

const createComponentRow = () => ({
  key: `${Date.now()}-${Math.random()}`,
  stockItem: null,
  quantity: "",
});

const createServiceRow = () => ({
  key: `${Date.now()}-${Math.random()}`,
  serviceItem: null,
});

const serializeRecipeState = (
  formState,
  componentState,
  resultingItem,
  serviceRows
) => {
  return JSON.stringify({
    form: {
      name: formState.name || "",
      description: formState.description || "",
      serialNumberPrefix: formState.serialNumberPrefix || "",
      startingSerialNumber: formState.startingSerialNumber || "",
      nextSerialNumber: formState.nextSerialNumber || "",
    },
    components: componentState.map((component) => ({
      stockItemId: component.stockItem?.id || null,
      quantity:
        component.quantity === undefined || component.quantity === null
          ? ""
          : component.quantity.toString(),
    })),
    resultingStockItemId: resultingItem?.id || null,
    serviceItems: (serviceRows || [])
      .map((row) => row?.serviceItem?.id || null)
      .filter(Boolean)
      .sort(),
  });
};

const parseOptionalNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeSerialPrefix = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value).trim();
};

const resolveStockItemImage = (stockItem) => {
  if (!stockItem || !stockItem.imageUrl) {
    return null;
  }
  return formatGoogleDriveMediaLink(stockItem.imageUrl);
};

const defaultFormState = {
  name: "",
  description: "",
  serialNumberPrefix: "",
  startingSerialNumber: "",
  nextSerialNumber: "",
};

export default function AddRecipe({
  open,
  onClose,
  onCompleted,
  initialRecipe = null,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form, setForm] = useState(defaultFormState);
  const [components, setComponents] = useState([createComponentRow()]);
  const [selectedResultingStockItem, setSelectedResultingStockItem] =
    useState(null);
  const [serviceRows, setServiceRows] = useState([createServiceRow()]);
  const [showComponents, setShowComponents] = useState(true);
  const [showServices, setShowServices] = useState(true);
  const [serviceItemOptions, setServiceItemOptions] = useState([]);
  const [isFetchingServiceItems, setIsFetchingServiceItems] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialSnapshot, setInitialSnapshot] = useState("");
  const isEditing = Boolean(initialRecipe?.id);
  const resultingImage = resolveStockItemImage(selectedResultingStockItem);
  const dialogTitle = isEditing
    ? t("manufacturing.editRecipe", "Edit manufacturing recipe")
    : t("manufacturing.addRecipe", "Add manufacturing recipe");
  const submitLabel = isEditing
    ? t("common.update", "Update")
    : t("manufacturing.saveRecipe", "Save recipe");
  const selectedComponentCount = useMemo(
    () => components.filter((component) => component.stockItem).length,
    [components]
  );
  const selectedServiceCount = useMemo(
    () => serviceRows.filter((row) => row.serviceItem).length,
    [serviceRows]
  );
  const buildRecipeCsvRows = () => {
    const rows = [
      [
        t("manufacturing.recipeName", "Recipe name"),
        form.name || "",
      ],
      [
        t("manufacturing.resultingStockItem", "Resulting stock item"),
        selectedResultingStockItem?.sku || initialRecipe?.StockItem?.sku || "",
      ],
      [
        t("manufacturing.serialPrefix", "Serial prefix"),
        form.serialNumberPrefix || "",
      ],
      [
        t("manufacturing.startingSerialNumber", "Starting serial number"),
        form.startingSerialNumber || "",
      ],
      [
        t("manufacturing.nextSerialNumber", "Next serial number"),
        form.nextSerialNumber || "",
      ],
      [],
    ];
    const header = [
      t("manufacturing.csvType", "Type"),
      t("stock.sku", "SKU"),
      t("manufacturing.itemName", "Item name"),
      t("stock.quantity", "Quantity"),
      t("manufacturing.costPrice", "Cost"),
    ];
    rows.push(header);
    components.forEach((component) => {
      if (!component.stockItem) {
        return;
      }
      const quantity = parseOptionalNumber(component.quantity) || "";
      const costPrice = Number(component.stockItem.costPrice);
      rows.push([
        t("manufacturing.components", "Components"),
        component.stockItem.sku || "",
        component.stockItem.name || "",
        quantity,
        Number.isFinite(costPrice) ? formatPrice(costPrice) : "",
      ]);
    });
    serviceRows.forEach((row) => {
      if (!row.serviceItem) {
        return;
      }
      const costPrice = Number(row.serviceItem.costPrice);
      rows.push([
        t("manufacturing.serviceItems", "Service items"),
        row.serviceItem.sku || "",
        row.serviceItem.name || "",
        "",
        Number.isFinite(costPrice) ? formatPrice(costPrice) : "",
      ]);
    });
    return rows;
  };
  const escapeCsvValue = (value) => {
    if (value === null || value === undefined) {
      return "";
    }
    const strValue = String(value);
    if (/[",\n]/.test(strValue)) {
      return `"${strValue.replace(/"/g, '""')}"`;
    }
    return strValue;
  };
  const handleExportRecipeCsv = () => {
    if (!selectedComponentCount && !selectedServiceCount) {
      dispatch(
        showPopupSnackbar({
          message: t(
            "manufacturing.recipeItemsRequired",
            "Add at least one component or service item."
          ),
          severity: "warning",
        })
      );
      return;
    }
    const rows = buildRecipeCsvRows();
    const csvContent = rows.map((row) => row.map(escapeCsvValue).join(",")).join("\n");
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    const filename = `${(form.name || initialRecipe?.name || "recipe")
      .replace(/[^a-z0-9-_]/gi, "_")
      .toLowerCase()}_${Date.now()}.csv`;
    link.download = filename;
    link.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);
  };
  const totalRecipeCost = useMemo(() => {
    const componentCost = components.reduce((sum, component) => {
      const quantity = parseOptionalNumber(component.quantity);
      const costPrice = Number(component.stockItem?.costPrice);
      if (!quantity || quantity <= 0 || !Number.isFinite(costPrice)) {
        return sum;
      }
      return sum + quantity * costPrice;
    }, 0);
    const serviceCost = serviceRows.reduce((sum, row) => {
      const costPrice = Number(row.serviceItem?.costPrice);
      if (!Number.isFinite(costPrice)) {
        return sum;
      }
      return sum + costPrice;
    }, 0);
    const total = componentCost + serviceCost;
    return Number.isFinite(total) ? total : 0;
  }, [components, serviceRows]);

  const resetForm = () => {
    const defaultComponents = [createComponentRow()];
    const defaultServiceRows = [createServiceRow()];
    const defaultForm = { ...defaultFormState };
    setForm(defaultForm);
    setComponents(defaultComponents);
    setSelectedResultingStockItem(null);
    setServiceRows(defaultServiceRows);
    setIsSubmitting(false);
    setInitialSnapshot(
      serializeRecipeState(
        defaultForm,
        defaultComponents,
        null,
        defaultServiceRows
      )
    );
  };

  const populateFromRecipe = (recipe) => {
    if (!recipe) {
      resetForm();
      return;
    }
    const recipeForm = {
      name: recipe.name || "",
      description: recipe.description || "",
      serialNumberPrefix: recipe.serialNumberPrefix?.toString() ?? "",
      startingSerialNumber: recipe.startingSerialNumber?.toString() ?? "",
      nextSerialNumber: recipe.nextSerialNumber?.toString() ?? "",
    };
    setForm(recipeForm);
    setSelectedResultingStockItem(recipe.StockItem || null);
    const normalizedComponentRows = recipe.ManufacturingRecipeStockItems?.map(
      (item, index) => ({
        key: `existing-${item.id || index}`,
        stockItem: item.StockItem || null,
        quantity:
          item.quantity === undefined || item.quantity === null
            ? ""
            : item.quantity.toString(),
      })
    ) || [createComponentRow()];
    setComponents(normalizedComponentRows);
    const normalizedServiceRows = recipe.ManufacturingRecipeServiceItems?.map(
      (entry, index) => ({
        key: `service-${entry.id || index}`,
        serviceItem: entry.ServiceItem || null,
      })
    ) || [createServiceRow()];
    setServiceRows(
      normalizedServiceRows.length
        ? normalizedServiceRows
        : [createServiceRow()]
    );
    setIsSubmitting(false);
    setInitialSnapshot(
      serializeRecipeState(
        recipeForm,
        normalizedComponentRows,
        recipe.StockItem || null,
        normalizedServiceRows
      )
    );
  };

  useEffect(() => {
    if (!open) {
      resetForm();
      return;
    }
    if (initialRecipe) {
      populateFromRecipe(initialRecipe);
    } else {
      resetForm();
    }
  }, [open, initialRecipe]);

  const handleComponentChange = (key, field, value) => {
    setComponents((prev) =>
      prev.map((component) =>
        component.key === key ? { ...component, [field]: value } : component
      )
    );
  };

  const handleComponentStockItemSelect = (key, stockItem) => {
    setComponents((prev) =>
      prev.map((component) =>
        component.key === key
          ? { ...component, stockItem: stockItem || null }
          : component
      )
    );
  };

  const handleServiceRowSelect = (key, serviceItem) => {
    setServiceRows((prev) => {
      if (
        serviceItem &&
        prev.some(
          (row) => row.key !== key && row.serviceItem?.id === serviceItem.id
        )
      ) {
        return prev;
      }
      return prev.map((row) =>
        row.key === key ? { ...row, serviceItem: serviceItem || null } : row
      );
    });
  };

  const addServiceRow = () => {
    setServiceRows((prev) => [...prev, createServiceRow()]);
  };

  const removeServiceRow = (key) => {
    setServiceRows((prev) => prev.filter((row) => row.key !== key));
  };

  const addComponentRow = () => {
    setComponents((prev) => [...prev, createComponentRow()]);
  };

  const removeComponentRow = (key) => {
    setComponents((prev) => prev.filter((component) => component.key !== key));
  };

  const normalizedComponents = useMemo(() => {
    return components
      .map((component) => {
        const stockItemId = component.stockItem?.id || null;
        const quantity = parseOptionalNumber(component.quantity);
        if (!stockItemId || !quantity || quantity <= 0) {
          return null;
        }
        return { stockItemId, quantity };
      })
      .filter(Boolean);
  }, [components]);

  const currentSnapshot = useMemo(
    () =>
      serializeRecipeState(
        form,
        components,
        selectedResultingStockItem,
        serviceRows
      ),
    [form, components, selectedResultingStockItem, serviceRows]
  );
  const hasChanges = initialSnapshot !== currentSnapshot;

  const fetchServiceItems = useCallback(
    (searchTerm = "") => {
      setIsFetchingServiceItems(true);
      fetch(server + "/get_service_items", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: getToken(),
          search: searchTerm,
          limit: 50,
        }),
      })
        .then((res) => res.json())
        .then((result) => {
          requestErrorHandler(result, dispatch);
          if (!result.error) {
            setServiceItemOptions(
              Array.isArray(result.serviceItems) ? result.serviceItems : []
            );
          }
        })
        .catch(() => {})
        .finally(() => setIsFetchingServiceItems(false));
    },
    [dispatch]
  );

  useEffect(() => {
    fetchServiceItems("");
  }, [fetchServiceItems]);

  const handleSubmit = () => {
    const trimmedName = form.name.trim();
    const resultingStockItemId = selectedResultingStockItem?.id || null;
    const normalizedServiceItems = serviceRows
      .map((row) => row.serviceItem?.id || null)
      .filter(Boolean);
    if (!trimmedName) {
      dispatch(
        showPopupSnackbar({
          message: t(
            "manufacturing.recipeNameRequired",
            "Recipe name is required."
          ),
          severity: "warning",
        })
      );
      return;
    }
    if (!resultingStockItemId) {
      dispatch(
        showPopupSnackbar({
          message: t(
            "manufacturing.resultingStockItemRequired",
            "Select a valid resulting stock item."
          ),
          severity: "warning",
        })
      );
      return;
    }
    if (!normalizedComponents.length && !normalizedServiceItems.length) {
      dispatch(
        showPopupSnackbar({
          message: t(
            "manufacturing.recipeItemsRequired",
            "Add at least one component or service item."
          ),
          severity: "warning",
        })
      );
      return;
    }

    setIsSubmitting(true);
    const payload = {
      token: getToken(),
      name: trimmedName,
      description: form.description?.trim() || undefined,
      resultingStockItemId,
      serialNumberPrefix: normalizeSerialPrefix(form.serialNumberPrefix),
      startingSerialNumber: parseOptionalNumber(form.startingSerialNumber),
      nextSerialNumber: parseOptionalNumber(form.nextSerialNumber),
      recipeStockItems: normalizedComponents,
      recipeServiceItems: normalizedServiceItems.map((serviceItemId) => ({
        serviceItemId,
      })),
    };
    if (isEditing && initialRecipe?.id) {
      payload.manufacturingRecipeId = initialRecipe.id;
    }

    fetch(
      server +
        (isEditing
          ? "/edit_manufacturing_recipe"
          : "/add_manufacturing_recipe"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    )
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (result.error) {
          dispatch(
            showPopupSnackbar({
              message:
                result.message ||
                t(
                  "manufacturing.recipeSaveFailed",
                  isEditing
                    ? "Failed to update manufacturing recipe."
                    : "Failed to create manufacturing recipe."
                ),
              severity: "error",
            })
          );
          return;
        }
        dispatch(
          showPopupSnackbar({
            message: isEditing
              ? t(
                  "manufacturing.recipeUpdated",
                  "Manufacturing recipe updated successfully."
                )
              : t(
                  "manufacturing.recipeCreated",
                  "Manufacturing recipe created successfully."
                ),
            severity: "success",
          })
        );
        if (onCompleted) {
          onCompleted(result.recipe);
        } else if (onClose) {
          onClose();
        }
        resetForm();
      })
      .catch(() => {
        dispatch(
          showPopupSnackbar({
            message: t(
              "manufacturing.recipeSaveFailed",
              isEditing
                ? "Failed to update manufacturing recipe."
                : "Failed to create manufacturing recipe."
            ),
            severity: "error",
          })
        );
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogAppBar header={dialogTitle} onClose={onClose} />
      <DialogContent style={{ paddingBottom: 120 }}>
        <Box mt={2} style={{ marginTop: 100 }}>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button
              variant="outlined"
              color="primary"
              style={{ width: 135, marginTop: 5 }}
              onClick={handleExportRecipeCsv}
              disabled={!selectedComponentCount && !selectedServiceCount}
              endIcon={<GetApp />}
            >
              {t("download") + " .csv"}
            </Button>
          </Box>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label={t("manufacturing.recipeName", "Recipe name")}
                    fullWidth
                    variant="outlined"
                    value={form.name}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        name: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("manufacturing.recipeDescription", "Description")}
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={3}
                    value={form.description}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        description: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  {selectedResultingStockItem ? (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      p={2}
                      border={1}
                      borderRadius={8}
                      borderColor="grey.300"
                    >
                      <Box display="flex" alignItems="center">
                        {resultingImage ? (
                          <Box
                            component="img"
                            src={resultingImage}
                            alt={selectedResultingStockItem.name}
                            width={72}
                            height={72}
                            mr={2}
                            style={{
                              objectFit: "cover",
                              borderRadius: 8,
                            }}
                          />
                        ) : null}
                        <Box>
                          <Typography variant="subtitle2">
                            {selectedResultingStockItem.sku}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {selectedResultingStockItem.name}
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setSelectedResultingStockItem(null)}
                      >
                        {t("common.change", "Change")}
                      </Button>
                    </Box>
                  ) : (
                    <StockItemSearch
                      allowNewItems={false}
                      clearOnSelect
                      disableGridWrapper
                      label={t(
                        "manufacturing.resultingStockItem",
                        "Resulting stock item"
                      )}
                      placeholder={t(
                        "manufacturing.searchStockItems",
                        "Search stock items"
                      )}
                      onChange={(_, value) => {
                        if (value && value.id) {
                          setSelectedResultingStockItem(value);
                          return;
                        }
                        setSelectedResultingStockItem(null);
                      }}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t("manufacturing.serialPrefix", "Serial prefix")}
                    fullWidth
                    variant="outlined"
                    value={form.serialNumberPrefix}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        serialNumberPrefix: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t(
                      "manufacturing.startingSerialNumber",
                      "Starting serial number"
                    )}
                    fullWidth
                    variant="outlined"
                    value={form.startingSerialNumber}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        startingSerialNumber: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t(
                      "manufacturing.nextSerialNumber",
                      "Next serial number"
                    )}
                    fullWidth
                    variant="outlined"
                    value={form.nextSerialNumber}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        nextSerialNumber: e.target.value,
                      })
                    }
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" flexDirection="column" height="100%">
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Title style={{ marginBottom: 8 }}>
                    {t("manufacturing.recipeItems", "Recipe Items")}
                  </Title>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t("manufacturing.recipeTotalCost", "Total cost")}: {" "}
                    {formatPrice(totalRecipeCost)}
                  </Typography>
                </Box>
                <Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box display="flex" alignItems="center" columnGap={8}>
                      <IconButton
                        onClick={() => setShowComponents((prev) => !prev)}
                        aria-label="Toggle components"
                      >
                        {showComponents ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                      <Title style={{ margin: 0 }}>
                        {t("manufacturing.components", "Components")} (
                        {selectedComponentCount})
                      </Title>
                    </Box>
                  </Box>
                  {showComponents ? (
                    <>
                      <Box
                        display="flex"
                        justifyContent="flex-end"
                        alignItems="center"
                        marginBottom={5}
                      >
                        <Button
                          startIcon={<AddIcon />}
                          color="primary"
                          variant="contained"
                          size="small"
                          onClick={addComponentRow}
                        >
                          {t("manufacturing.addComponent", "Add component")}
                        </Button>
                      </Box>
                      <List dense>
                        {components.map((component) => {
                          const disableDelete = false;
                          return (
                            <ListItem
                              key={component.key}
                              divider
                              alignItems="flex-start"
                              style={{ paddingLeft: 0, paddingRight: 0 }}
                            >
                              {component.stockItem ? (
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  flex={1}
                                >
                                  {component.stockItem.imageUrl ? (
                                    <Box
                                      component="img"
                                      src={resolveStockItemImage(
                                        component.stockItem
                                      )}
                                      alt={
                                        component.stockItem.name ||
                                        component.stockItem.sku
                                      }
                                      width={56}
                                      height={56}
                                      mr={2}
                                      style={{
                                        objectFit: "cover",
                                        borderRadius: 6,
                                      }}
                                    />
                                  ) : null}
                                  <ListItemText
                                    primary={
                                      component.stockItem.sku ||
                                      t("common.notAvailable", "N/A")
                                    }
                                    secondary={
                                      <Box>
                                        <Typography
                                          variant="body2"
                                          color="textSecondary"
                                        >
                                          {component.stockItem.name ||
                                            t("common.notAvailable", "N/A")}
                                        </Typography>
                                        {component.stockItem.costPrice !=
                                        null ? (
                                          <Typography
                                            variant="caption"
                                            color="textSecondary"
                                          >
                                            {t(
                                              "manufacturing.costPrice",
                                              "Cost"
                                            )}{" "}
                                            {formatPrice(
                                              component.stockItem.costPrice
                                            )}
                                          </Typography>
                                        ) : null}
                                      </Box>
                                    }
                                  />
                                </Box>
                              ) : (
                                <Box flex={1} mr={2}>
                                  <StockItemSearch
                                    allowNewItems={false}
                                    clearOnSelect
                                    disableGridWrapper
                                    label={t(
                                      "manufacturing.componentStockItem",
                                      "Component stock item"
                                    )}
                                    placeholder={t(
                                      "manufacturing.searchStockItems",
                                      "Search stock items"
                                    )}
                                    onChange={(_, value) => {
                                      if (value && value.id) {
                                        handleComponentStockItemSelect(
                                          component.key,
                                          value
                                        );
                                      } else {
                                        handleComponentStockItemSelect(
                                          component.key,
                                          null
                                        );
                                      }
                                    }}
                                  />
                                </Box>
                              )}
                              <Box
                                display="flex"
                                alignItems="center"
                                columnGap={8}
                                ml={component.stockItem ? 2 : 0}
                              >
                                {component.stockItem ? (
                                  <TextField
                                    label={t("stock.quantity", "Quantity")}
                                    variant="outlined"
                                    type="number"
                                    size="small"
                                    value={component.quantity}
                                    onChange={(e) =>
                                      handleComponentChange(
                                        component.key,
                                        "quantity",
                                        e.target.value
                                      )
                                    }
                                    style={{ width: 120 }}
                                  />
                                ) : null}
                                <IconButton
                                  aria-label={t("common.delete", "Delete")}
                                  onClick={() =>
                                    removeComponentRow(component.key)
                                  }
                                  disabled={disableDelete}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </ListItem>
                          );
                        })}
                      </List>
                    </>
                  ) : null}
                </Box>
                <Box mt={4}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box display="flex" alignItems="center" columnGap={8}>
                      <IconButton
                        onClick={() => setShowServices((prev) => !prev)}
                        aria-label="Toggle services"
                      >
                        {showServices ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                      <Title style={{ margin: 0 }}>
                        {t("manufacturing.serviceItems", "Service items")} (
                        {selectedServiceCount})
                      </Title>
                    </Box>
                  </Box>
                  {showServices ? (
                    <>
                      <Box
                        display="flex"
                        justifyContent="flex-end"
                        alignItems="center"
                        marginBottom={5}
                      >
                        {" "}
                        <Button
                          startIcon={<AddIcon />}
                          color="primary"
                          variant="contained"
                          size="small"
                          onClick={addServiceRow}
                        >
                          {t(
                            "serviceItemsModule.addServiceItem",
                            "Add service item"
                          )}
                        </Button>
                      </Box>
                      <List dense>
                        {serviceRows.map((row) => {
                          const disableDelete = false;
                          return (
                            <ListItem
                              key={row.key}
                              divider
                              alignItems="flex-start"
                              style={{ paddingLeft: 0, paddingRight: 0 }}
                            >
                              {row.serviceItem ? (
                                <ListItemText
                                  primary={
                                    row.serviceItem.sku ||
                                    t("common.notAvailable", "N/A")
                                  }
                                  secondary={
                                    <Box>
                                      {row.serviceItem.imageUrl ? (
                                        <Box
                                          component="img"
                                          src={resolveStockItemImage(
                                            row.serviceItem
                                          )}
                                          alt={
                                            row.serviceItem.name ||
                                            row.serviceItem.sku
                                          }
                                          width={56}
                                          height={56}
                                          mb={1}
                                          style={{
                                            objectFit: "cover",
                                            borderRadius: 6,
                                          }}
                                        />
                                      ) : null}
                                      <Typography variant="body2">
                                        {row.serviceItem.name ||
                                          t("common.notAvailable", "N/A")}
                                      </Typography>
                                      {row.serviceItem.costPrice != null ? (
                                        <Typography
                                          variant="caption"
                                          color="textSecondary"
                                        >
                                          {t(
                                            "manufacturing.serviceItemCost",
                                            "Cost"
                                          )}
                                          {": "}
                                          {row.serviceItem.costPrice}
                                        </Typography>
                                      ) : null}
                                    </Box>
                                  }
                                />
                              ) : (
                                <Box flex={1} mr={2}>
                                  <Autocomplete
                                    options={serviceItemOptions}
                                    getOptionSelected={(option, value) =>
                                      option.id === value.id
                                    }
                                    getOptionLabel={(option) =>
                                      option?.sku
                                        ? [option.sku, option.name]
                                            .filter(Boolean)
                                            .join(" \u2013 ")
                                        : ""
                                    }
                                    loading={isFetchingServiceItems}
                                    value={row.serviceItem || null}
                                    onChange={(_, value) =>
                                      handleServiceRowSelect(row.key, value)
                                    }
                                    onInputChange={(_, value, reason) => {
                                      if (reason === "input") {
                                        const trimmed = (value || "").trim();
                                        fetchServiceItems(trimmed);
                                      }
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        variant="outlined"
                                        size="small"
                                        label={t(
                                          "manufacturing.searchServiceItems",
                                          "Search service items"
                                        )}
                                        placeholder={t(
                                          "manufacturing.searchServiceItems",
                                          "Search service items"
                                        )}
                                      />
                                    )}
                                    noOptionsText={t(
                                      "manufacturing.noServiceItemsFound",
                                      "No service items found"
                                    )}
                                  />
                                </Box>
                              )}
                              <IconButton
                                aria-label={t("common.delete", "Delete")}
                                onClick={() => removeServiceRow(row.key)}
                                disabled={disableDelete}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </>
                  ) : null}
                  {!serviceRows.some((row) => row.serviceItem) ? (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      style={{ display: "block", marginTop: 8 }}
                    >
                      {t(
                        "manufacturing.noServiceItemsConfigured",
                        "No service items added to this recipe."
                      )}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Box height={100} />
        <Box
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            padding: 16,
            backgroundColor: "white",
            boxShadow: "0px -4px 12px rgba(0, 0, 0, 0.1)",
            zIndex: 10,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Button
                color="primary"
                variant="contained"
                onClick={handleSubmit}
                disabled={isSubmitting || !hasChanges}
              >
                {isSubmitting ? t("common.saving", "Saving…") : submitLabel}
              </Button>
            </Grid>
            <Grid item>
              <Button
                onClick={onClose}
                disabled={isSubmitting}
                variant="outlined"
              >
                {t("common.cancel", "Cancel")}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
